import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../utils/functions/api';
import { aiPlanLabel, useAiProviderOptions } from './useAiProviderOptions';

const API = process.env.REACT_APP_API_URL;
const PLAN_COLOR = { free: '#059669', paid: '#d97706', '': '#64748b' };

// ตัวเลือก AI (โมดูลใช้ซ้ำ) — ปุ่มเปิดหน้าต่างให้ user เลือกว่าจะใช้ AI ตัวไหน ดูว่าฟรี/เสียเงิน
// และกรอก/แก้ข้อมูลของ AI นั้นได้เลย (แผนฟรี/เสียเงิน, เพิ่ม API key) ไม่ต้องไปหน้านักพัฒนา
//
// วิธีใช้ที่จุดไหนก็ได้:
//   <AiProviderPicker value={providerKey} onChange={setProviderKey} storageKey="my_feature_ai" uiLang={uiLang} />
// - value: provider_key ที่เลือก ('' = อัตโนมัติ ให้ระบบเลือกเอง) — ส่งต่อไปให้ backend ของฟีเจอร์นั้นใช้
// - storageKey (ไม่บังคับ): ถ้าใส่ ค่าที่เลือกจะถูกจำใน localStorage ใช้ loadSavedAiProvider(storageKey) อ่านค่าเริ่มต้น
// - compact: ปุ่มขนาดเล็ก (default true)
export const loadSavedAiProvider = (storageKey) => {
  try {
    return (storageKey && localStorage.getItem(storageKey)) || '';
  } catch {
    return '';
  }
};

export function AiProviderPicker({ value = '', onChange, storageKey, uiLang = 'th', compact = true }) {
  const U = (th, en) => (uiLang === 'en' ? en : th);
  const { options, loading, error, reload } = useAiProviderOptions();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [editingKey, setEditingKey] = useState('');
  const [keyForm, setKeyForm] = useState({ label: '', value: '' });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (open) {
      setDraft(value);
      setEditingKey('');
      setMessage('');
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const current = options.find((o) => o.provider_key === value);
  const buttonText = value
    ? `AI: ${current?.display_name || value}${current ? ` (${aiPlanLabel(current.plan, uiLang)})` : ''}`
    : U('AI: อัตโนมัติ', 'AI: Auto');

  const confirm = () => {
    onChange?.(draft);
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, draft || '');
      } catch {
        /* localStorage ใช้ไม่ได้ก็แค่ไม่จำค่า */
      }
    }
    setOpen(false);
  };

  const savePlan = async (providerKey, plan) => {
    setBusy(true);
    setMessage('');
    try {
      await api.put(`${API}/api-provider/by-key/${providerKey}/plan`, { plan });
      await reload();
    } catch (e) {
      setMessage(e?.response?.data?.message || e?.message || U('บันทึกไม่สำเร็จ', 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const addKey = async (opt) => {
    if (!keyForm.value.trim()) return;
    setBusy(true);
    setMessage('');
    try {
      // provider ที่ใช้ key จาก .env อยู่ยังไม่มี record ใน DB — ตั้งแผนก่อนเพื่อสร้าง record แล้วค่อยเพิ่ม key
      const { data: ensured } = await api.put(`${API}/api-provider/by-key/${opt.provider_key}/plan`, {
        plan: opt.plan,
      });
      await api.post(`${API}/api-provider/${ensured.id}/keys`, {
        label: keyForm.label.trim(),
        value: keyForm.value.trim(),
      });
      setKeyForm({ label: '', value: '' });
      setMessage(U('เพิ่ม API key แล้ว', 'API key added'));
      await reload();
    } catch (e) {
      setMessage(e?.response?.data?.message || e?.message || U('เพิ่ม key ไม่สำเร็จ', 'Add key failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ fontSize: compact ? '0.68rem' : '0.8rem', minWidth: 0, py: compact ? 0 : 0.5 }}
      >
        🤖 {buttonText}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          {U('เลือก AI ที่ใช้', 'Choose AI provider')}
        </DialogTitle>
        <DialogContent dividers>
          {loading && options.length === 0 ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption">{U('กำลังโหลด...', 'Loading...')}</Typography>
            </Stack>
          ) : (
            <>
              {error && (
                <Typography variant="caption" sx={{ color: '#b91c1c', display: 'block', mb: 1 }}>
                  {error}
                </Typography>
              )}
              <Box
                onClick={() => setDraft('')}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', py: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#f8fafc' } }}
              >
                <Radio size="small" checked={draft === ''} />
                <Box>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    {U('อัตโนมัติ', 'Auto')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {U('ให้ระบบเลือกเอง และสลับตัวสำรองให้ถ้าตัวแรกใช้ไม่ได้', 'System picks and falls back automatically')}
                  </Typography>
                </Box>
              </Box>

              {options.map((opt) => (
                <Box key={opt.provider_key} sx={{ borderTop: '1px solid #e2e8f0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                    <Box
                      onClick={() => setDraft(opt.provider_key)}
                      sx={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: '#f8fafc' } }}
                    >
                      <Radio size="small" checked={draft === opt.provider_key} />
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{opt.display_name}</Typography>
                          <Chip
                            label={aiPlanLabel(opt.plan, uiLang)}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, color: '#fff', bgcolor: PLAN_COLOR[opt.plan] }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {opt.default_model ? `${U('โมเดล', 'Model')}: ${opt.default_model} · ` : ''}
                          {opt.key_count} {U('key ที่ใช้ได้', 'active key(s)')}
                        </Typography>
                      </Box>
                    </Box>
                    <Tooltip title={U('กรอก/แก้ข้อมูล AI นี้', 'Edit this AI')}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingKey((k) => (k === opt.provider_key ? '' : opt.provider_key));
                          setKeyForm({ label: '', value: '' });
                          setMessage('');
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {editingKey === opt.provider_key && (
                    <Box sx={{ pl: 4.5, pr: 1, pb: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                        {U('แผนบัญชีของ AI นี้', 'Plan of this AI')}
                      </Typography>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={opt.plan || null}
                        disabled={busy}
                        onChange={(_, v) => savePlan(opt.provider_key, v || '')}
                        sx={{ mb: 1.25 }}
                      >
                        <ToggleButton value="free" sx={{ fontSize: '0.72rem', px: 1.5 }}>
                          {U('ฟรี', 'Free')}
                        </ToggleButton>
                        <ToggleButton value="paid" sx={{ fontSize: '0.72rem', px: 1.5 }}>
                          {U('เสียเงิน', 'Paid')}
                        </ToggleButton>
                      </ToggleButtonGroup>

                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                        {U('เพิ่ม API key', 'Add API key')}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          placeholder={U('ชื่อเรียก (ไม่บังคับ)', 'Label (optional)')}
                          value={keyForm.label}
                          onChange={(e) => setKeyForm((f) => ({ ...f, label: e.target.value }))}
                          sx={{ width: 150 }}
                        />
                        <TextField
                          size="small"
                          type="password"
                          autoComplete="new-password"
                          placeholder="API key"
                          value={keyForm.value}
                          onChange={(e) => setKeyForm((f) => ({ ...f, value: e.target.value }))}
                          sx={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busy || !keyForm.value.trim()}
                          onClick={() => addKey(opt)}
                        >
                          {U('เพิ่ม', 'Add')}
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Box>
              ))}

              {options.length === 0 && !loading && (
                <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                  {U('ยังไม่มี AI ที่ใช้งานได้ — เพิ่ม API key ที่หน้านักพัฒนา', 'No AI available — add an API key on the developer page')}
                </Typography>
              )}

              {message && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#334155' }}>
                  {message}
                </Typography>
              )}
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
                {U('จัดการ key/โมเดลทั้งหมดได้ที่หน้านักพัฒนา (/management/developer)', 'Manage all keys/models on the developer page (/management/developer)')}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{U('ยกเลิก', 'Cancel')}</Button>
          <Button variant="contained" onClick={confirm}>
            {U('ใช้ตัวที่เลือก', 'Use selected')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AiProviderPicker;
